import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FileText, Download, ArrowLeft, Building2, Receipt, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';
const API_URL = `${BACKEND_URL}/api`;

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [invoiceData, setInvoiceData] = useState(null);
  const [showDataForm, setShowDataForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    company_name: '',
    nip: '',
    address: '',
    postal_code: '',
    city: '',
    email: '',
  });

  useEffect(() => {
    loadInvoiceData();
    loadInvoices();
  }, []);

  const loadInvoiceData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/invoice-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data) {
        setInvoiceData(response.data);
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    }
  };

  const handleSaveInvoiceData = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/invoice-data`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Dane fakturowe zapisane');
      setShowDataForm(false);
      loadInvoiceData();
    } catch (error) {
      toast.error('Błąd zapisu danych');
      console.error('Error saving invoice data:', error);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `faktura_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Faktura pobrana');
    } catch (error) {
      toast.error('Błąd pobierania faktury');
      console.error('Error downloading invoice:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-900 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-700/50 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="icon" className="rounded-xl dark:text-slate-200 dark:hover:bg-slate-700">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-lg">
                <Receipt className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl text-primary-900 dark:text-slate-100 font-bold">Faktury VAT</h1>
                <p className="text-sm text-muted-foreground dark:text-slate-400">Zarządzaj swoimi fakturami</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Invoice Data Card */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700 p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold">Dane Fakturowe</h2>
                <p className="text-sm text-muted-foreground dark:text-slate-400">Informacje do wystawiania faktur</p>
              </div>
            </div>
            {invoiceData && !showDataForm && (
              <Button
                onClick={() => setShowDataForm(true)}
                data-testid="edit-invoice-data"
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl shadow-md"
              >
                Edytuj Dane
              </Button>
            )}
          </div>

          {invoiceData && !showDataForm ? (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-gradient-to-br from-blue-50 to-slate-50 dark:from-slate-700 dark:to-slate-700 rounded-xl">
                <p className="text-xs text-muted-foreground dark:text-slate-400 mb-1 uppercase tracking-wide">Nazwa firmy</p>
                <p className="font-semibold text-primary-900 dark:text-slate-100 text-lg">{invoiceData.company_name}</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-50 to-slate-50 dark:from-slate-700 dark:to-slate-700 rounded-xl">
                <p className="text-xs text-muted-foreground dark:text-slate-400 mb-1 uppercase tracking-wide">NIP</p>
                <p className="font-semibold text-primary-900 dark:text-slate-100 text-lg font-mono">{invoiceData.nip}</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-50 to-slate-50 dark:from-slate-700 dark:to-slate-700 rounded-xl">
                <p className="text-xs text-muted-foreground dark:text-slate-400 mb-1 uppercase tracking-wide">Adres</p>
                <p className="font-semibold text-primary-900 dark:text-slate-100">{invoiceData.address}</p>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-50 to-slate-50 dark:from-slate-700 dark:to-slate-700 rounded-xl">
                <p className="text-xs text-muted-foreground dark:text-slate-400 mb-1 uppercase tracking-wide">Miasto</p>
                <p className="font-semibold text-primary-900 dark:text-slate-100">
                  {invoiceData.postal_code} {invoiceData.city}
                </p>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-50 to-slate-50 dark:from-slate-700 dark:to-slate-700 rounded-xl md:col-span-2">
                <p className="text-xs text-muted-foreground dark:text-slate-400 mb-1 uppercase tracking-wide">Email</p>
                <p className="font-semibold text-primary-900 dark:text-slate-100">{invoiceData.email}</p>
              </div>
            </div>
          ) : showDataForm ? (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="company_name" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nazwa firmy *</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Nazwa Sp. z o.o."
                    className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <div>
                  <Label htmlFor="nip" className="text-sm font-semibold text-slate-700 dark:text-slate-300">NIP *</Label>
                  <Input
                    id="nip"
                    value={formData.nip}
                    onChange={(e) => setFormData({ ...formData, nip: e.target.value })}
                    placeholder="1234567890"
                    className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <div>
                  <Label htmlFor="invoice_email" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Email *</Label>
                  <Input
                    id="invoice_email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="faktury@firma.pl"
                    className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="address" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Adres *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="ul. Przykładowa 123"
                    className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <div>
                  <Label htmlFor="postal_code" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kod pocztowy *</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="00-001"
                    className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
                <div>
                  <Label htmlFor="city" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Miasto *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Warszawa"
                    className="mt-2 h-12 rounded-xl dark:bg-slate-700 dark:border-slate-600"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSaveInvoiceData}
                  className="flex-1 bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 text-white rounded-xl h-12"
                  data-testid="save-invoice-data-button"
                >
                  Zapisz dane
                </Button>
                {invoiceData && (
                  <Button
                    onClick={() => setShowDataForm(false)}
                    variant="outline"
                    className="rounded-xl h-12 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
                  >
                    Anuluj
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-slate-400 dark:text-slate-500" />
              </div>
              <p className="text-muted-foreground dark:text-slate-400 mb-6 text-lg">
                Dodaj swoje dane fakturowe, aby otrzymywać faktury VAT automatycznie
              </p>
              <Button
                onClick={() => setShowDataForm(true)}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl shadow-lg h-12 px-8"
              >
                Dodaj Dane Fakturowe
              </Button>
            </div>
          )}
        </div>

        {/* Invoices List */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-slate-700 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
              <Receipt className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold">Historia Faktur</h2>
              <p className="text-sm text-muted-foreground dark:text-slate-400">{invoices.length} {invoices.length === 1 ? 'faktura' : 'faktur'}</p>
            </div>
          </div>

          {invoices.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 flex items-center justify-center mx-auto mb-6">
                <Receipt className="w-12 h-12 text-slate-300 dark:text-slate-500" />
              </div>
              <p className="text-muted-foreground dark:text-slate-400 text-lg mb-2">
                Nie masz jeszcze żadnych faktur
              </p>
              <p className="text-sm text-muted-foreground dark:text-slate-500">
                Faktury są generowane automatycznie po płatności
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {invoices.map((invoice) => (
                <div
                  key={invoice.id}
                  data-testid={`invoice-${invoice.id}`}
                  className="group p-6 border-2 border-slate-100 dark:border-slate-600 rounded-2xl hover:border-primary-200 dark:hover:border-primary-600 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-slate-50 dark:from-slate-700 dark:to-slate-700"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900 dark:to-green-800 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="font-mono font-bold text-primary-900 dark:text-slate-100 text-lg">{invoice.invoice_number}</p>
                        <p className="text-sm text-muted-foreground dark:text-slate-400">
                          {new Date(invoice.issue_date).toLocaleDateString('pl-PL', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground dark:text-slate-400">Kwota brutto</p>
                        <p className="text-2xl font-bold text-primary-900 dark:text-slate-100">
                          {invoice.gross_amount.toFixed(2)} <span className="text-lg">{invoice.currency}</span>
                        </p>
                      </div>
                      {invoice.status === 'paid' && (
                        <div className="px-4 py-2 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 rounded-xl flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="text-sm font-semibold">Opłacona</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-white dark:bg-slate-800 rounded-xl">
                    <div>
                      <p className="text-xs text-muted-foreground dark:text-slate-400 mb-1">Netto</p>
                      <p className="font-semibold text-slate-700 dark:text-slate-200">{invoice.net_amount.toFixed(2)} {invoice.currency}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground dark:text-slate-400 mb-1">VAT 23%</p>
                      <p className="font-semibold text-slate-700 dark:text-slate-200">{invoice.vat_amount.toFixed(2)} {invoice.currency}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground dark:text-slate-400 mb-1">Opis</p>
                      <p className="font-semibold text-slate-700 dark:text-slate-200 text-sm truncate">{invoice.description}</p>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleDownloadInvoice(invoice.id)}
                    data-testid={`download-invoice-${invoice.id}`}
                    className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-xl h-11 group-hover:shadow-lg transition-all"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Pobierz fakturę PDF
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
